// ../../react-shim.js
import React from "react";

// src/alert-dialog.tsx
import React2 from "react";
import { jsx } from "react/jsx-runtime";
var AlertDialog = (props) => {
  const { leastDestructiveRef, ...rest } = props;
  return /* @__PURE__ */ jsx(Modal, {
    ...rest,
    initialFocusRef: leastDestructiveRef
  });
};
var AlertDialogContent = React2.forwardRef(
  (props, ref) => /* @__PURE__ */ jsx(ModalContent, {
    ...props,
    ref,
    role: "alertdialog"
  })
);

// src/modal.tsx
import { CloseButton } from "@nature-ui/close-button";
import { FocusLock } from "@nature-ui/focus-lock";
import { Portal } from "@nature-ui/portal";
import { createContext } from "@nature-ui/react-utils";
import {
  clsx,
  forwardRef,
  nature as nature2
} from "@nature-ui/system";
import { fadeConfig } from "@nature-ui/transition";
import {
  callAllHandler as callAllHandler2,
  cx,
  __DEV__
} from "@nature-ui/utils";
import {
  AnimatePresence,
  motion as motion2,
  usePresence
} from "framer-motion";
import React6 from "react";
import { RemoveScroll } from "react-remove-scroll";

// src/modal-transition.tsx
import { nature } from "@nature-ui/system";
import { scaleFadeConfig, slideFadeConfig } from "@nature-ui/transition";
import { motion } from "framer-motion";
import React3 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var transition = {
  slideInBottom: {
    ...slideFadeConfig,
    custom: { offsetY: 16, reverse: true }
  },
  slideInRight: {
    ...slideFadeConfig,
    custom: { offsetX: 16, reverse: true }
  },
  slideInLeft: {
    ...slideFadeConfig,
    custom: { offsetX: -16, reverse: true }
  },
  scale: {
    ...scaleFadeConfig,
    custom: { initialScale: 0.95, reverse: true }
  },
  none: {}
};
var Motion = nature(motion.section);
var ModalTransition = React3.forwardRef(
  (props, ref) => {
    const { preset, ...rest } = props;
    const motionProps = transition[preset];
    return /* @__PURE__ */ jsx2(Motion, {
      ref,
      ...motionProps,
      ...rest
    });
  }
);

// src/use-modal.ts
import { useIds } from "@nature-ui/hooks";
import { callAllHandler, mergeRefs } from "@nature-ui/utils";
import { hideOthers } from "aria-hidden";
import React5 from "react";

// src/modal-manager.ts
import React4 from "react";
var ModalManager = class {
  modals;
  constructor() {
    this.modals = [];
  }
  add(modal) {
    this.modals.push(modal);
  }
  remove(modal) {
    this.modals = this.modals.filter((_modal) => _modal !== modal);
  }
  isTopModal(modal) {
    const topmostModal = this.modals[this.modals.length - 1];
    return topmostModal === modal;
  }
};
var manager = new ModalManager();
var useModalManager = (ref, isOpen) => {
  React4.useEffect(() => {
    if (isOpen) {
      manager.add(ref);
    }
    return () => {
      manager.remove(ref);
    };
  }, [isOpen, ref]);
};

// src/use-modal.ts
var useAriaHidden = (ref, shouldHide) => {
  const currentElement = ref.current;
  React5.useEffect(() => {
    if (!ref.current || !shouldHide)
      return void 0;
    return hideOthers(ref.current);
  }, [shouldHide, ref, currentElement]);
};
var useModal = (props) => {
  const {
    isOpen,
    onClose,
    id,
    closeOnOverlayClick = true,
    closeOnEsc = true,
    useInert = true,
    onOverlayClick: onOverlayClickProp,
    onEsc,
    scrollBehavior,
    isCentered
  } = props;
  const dialogRef = React5.useRef(null);
  const overlayRef = React5.useRef(null);
  const [dialogId, headerId, bodyId] = useIds(
    id,
    "nature-modal",
    "nature-modal--header",
    "nature-modal--body"
  );
  useAriaHidden(dialogRef, isOpen && useInert);
  useModalManager(dialogRef, isOpen);
  const mouseDownTarget = React5.useRef(null);
  const onMouseDown = React5.useCallback((event) => {
    mouseDownTarget.current = event.target;
  }, []);
  const onKeyDown = React5.useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        if (closeOnEsc) {
          onClose == null ? void 0 : onClose();
        }
        onEsc == null ? void 0 : onEsc();
      }
    },
    [closeOnEsc, onClose, onEsc]
  );
  const onOverlayClick = React5.useCallback(
    (event) => {
      event.stopPropagation();
      if (mouseDownTarget.current !== event.target)
        return;
      if (!manager.isTopModal(dialogRef))
        return;
      if (closeOnOverlayClick) {
        onClose == null ? void 0 : onClose();
      }
      onOverlayClickProp == null ? void 0 : onOverlayClickProp();
    },
    [onClose, closeOnOverlayClick, onOverlayClickProp]
  );
  const [headerMounted, setHeaderMounted] = React5.useState(false);
  const [bodyMounted, setBodyMounted] = React5.useState(false);
  const getDialogProps = React5.useCallback(
    (props2 = {}, ref = null) => ({
      role: "dialog",
      ...props2,
      ref: mergeRefs(ref, dialogRef),
      id: dialogId,
      tabIndex: -1,
      "aria-modal": true,
      "aria-labelledby": headerMounted ? headerId : void 0,
      "aria-describedby": bodyMounted ? bodyId : void 0,
      onClick: callAllHandler(
        props2.onClick,
        (event) => event.stopPropagation()
      )
    }),
    [bodyId, bodyMounted, dialogId, headerId, headerMounted]
  );
  const getDialogContainerProps = React5.useCallback(
    (props2 = {}, ref = null) => ({
      ...props2,
      ref: mergeRefs(ref, overlayRef),
      onClick: callAllHandler(props2.onClick, onOverlayClick),
      onKeyDown: callAllHandler(props2.onKeyDown, onKeyDown),
      onMouseDown: callAllHandler(props2.onMouseDown, onMouseDown)
    }),
    [onKeyDown, onMouseDown, onOverlayClick]
  );
  return {
    isOpen,
    onClose,
    headerId,
    bodyId,
    setBodyMounted,
    setHeaderMounted,
    dialogRef,
    overlayRef,
    getDialogProps,
    getDialogContainerProps,
    scrollBehavior,
    isCentered
  };
};

// src/modal.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var [ModalContextProvider, useModalContext] = createContext({
  strict: true,
  name: "ModalContext",
  errorMessage: "The `ModalContext` is not available outside the `Modal` component."
});
var NatureModal = (props) => {
  const {
    portalProps,
    children,
    autoFocus,
    trapFocus,
    initialFocusRef,
    finalFocusRef,
    returnFocusOnClose,
    blockScrollOnMount,
    allowPinchZoom,
    preserveScrollBarGap,
    motionPreset,
    lockFocusAcrossFrames,
    size,
    variant,
    scrollBehavior
  } = props;
  const modal = useModal(props);
  const context = {
    ...modal,
    scrollBehavior,
    autoFocus,
    trapFocus,
    initialFocusRef,
    finalFocusRef,
    returnFocusOnClose,
    blockScrollOnMount,
    allowPinchZoom,
    preserveScrollBarGap,
    motionPreset,
    lockFocusAcrossFrames,
    variant,
    size
  };
  return /* @__PURE__ */ jsx3(ModalContextProvider, {
    value: context,
    children: /* @__PURE__ */ jsx3(AnimatePresence, {
      children: context.isOpen && /* @__PURE__ */ jsx3(Portal, {
        ...portalProps,
        children
      })
    })
  });
};
NatureModal.defaultProps = {
  lockFocusAcrossFrames: true,
  returnFocusOnClose: true,
  scrollBehavior: "outside",
  trapFocus: true,
  autoFocus: true,
  blockScrollOnMount: true,
  allowPinchZoom: false,
  motionPreset: "scale"
};
if (__DEV__) {
  NatureModal.displayName = "Modal";
}
var ModalFocusScope = (props) => {
  const {
    autoFocus,
    trapFocus,
    dialogRef,
    initialFocusRef,
    blockScrollOnMount,
    allowPinchZoom,
    finalFocusRef,
    returnFocusOnClose,
    preserveScrollBarGap,
    lockFocusAcrossFrames
  } = useModalContext();
  const [isPresent, safeToRemove] = usePresence();
  React6.useEffect(() => {
    if (!isPresent) {
      setTimeout(() => safeToRemove == null ? void 0 : safeToRemove(), 0);
    }
  }, [isPresent, safeToRemove]);
  return /* @__PURE__ */ jsx3(FocusLock, {
    autoFocus,
    isDisabled: !trapFocus,
    initialFocusRef,
    finalFocusRef,
    restoreFocus: returnFocusOnClose,
    contentRef: dialogRef,
    lockFocusAcrossFrames,
    children: /* @__PURE__ */ jsx3(RemoveScroll, {
      removeScrollBar: !preserveScrollBarGap,
      allowPinchZoom,
      enabled: blockScrollOnMount,
      forwardProps: true,
      children: props.children
    })
  });
};
if (__DEV__) {
  ModalFocusScope.displayName = "ModalFocusScope";
}
var _SIZES = {
  full: { maxW: "max-w-screen", maxH: "max-h-screen h-screen" },
  lg: { maxW: "max-w-2xl" },
  md: { maxW: "max-w-lg" },
  sm: { maxW: "max-w-md" },
  xl: { maxW: "max-w-4xl" },
  xs: { maxW: "max-w-xs" }
};
var ModalContent = forwardRef(
  (props, ref) => {
    const { children, containerProps: rootProps, className, ...rest } = props;
    const {
      getDialogProps,
      getDialogContainerProps,
      scrollBehavior,
      size = "md",
      isCentered
    } = useModalContext();
    const dialogProps = getDialogProps(rest, ref);
    const containerProps = getDialogContainerProps(rootProps);
    const { motionPreset } = useModalContext();
    const dialogContainerClassnames = clsx(
      "nature-modal__content-container",
      "min-h-screen w-screen h-screen flex fixed left-0 top-0 z-50 justify-center",
      {
        ["overflow-auto"]: scrollBehavior === "outside",
        ["overflow-hidden"]: scrollBehavior === "inside" || size === "full",
        ["items-center"]: isCentered,
        ["items-start"]: !isCentered
      }
    );
    const dialogClassnames = clsx(
      "nature-modal__content",
      "flex flex-col relative w-full outline-none bg-white",
      className,
      {
        [_SIZES[size].maxH]: size in _SIZES && size === "full",
        [_SIZES[size].maxW]: size in _SIZES,
        ["my-14 rounded"]: size != "full"
      }
    );
    const css = {
      maxHeight: scrollBehavior === "inside" && size !== "full" ? "calc(100vh - 7.5rem)" : void 0
    };
    return /* @__PURE__ */ jsx3(ModalFocusScope, {
      children: /* @__PURE__ */ jsx3(nature2.div, {
        ...containerProps,
        className: dialogContainerClassnames,
        tabIndex: 1,
        children: /* @__PURE__ */ jsx3(ModalTransition, {
          preset: motionPreset,
          className: dialogClassnames,
          css: { ...css, ...props.css },
          ...dialogProps,
          children
        })
      })
    });
  }
);
var MotionDiv = nature2(motion2.div);
var ModalOverlay = forwardRef(
  (props, ref) => {
    const { className, ...rest } = props;
    const { motionPreset, variant = "blur" } = useModalContext();
    const motionProps = motionPreset === "none" ? {} : fadeConfig;
    const _variant = variant === "blur" ? "5px" : "0px";
    const css = {
      backgroundColor: "rgba(0,0,0,0.5)",
      backdropFilter: `blur(${_variant})`
    };
    const _className = cx(
      "nature-modal__overlay",
      "fixed left-0 top-0 w-screen h-screen z-50",
      className
    );
    return /* @__PURE__ */ jsx3(MotionDiv, {
      ...motionProps,
      ref,
      className: _className,
      css: { ...css, ...props.css },
      ...rest
    });
  }
);
if (__DEV__) {
  ModalOverlay.displayName = "ModalOverlay";
}
var ModalHeader = forwardRef(
  (props, ref) => {
    const { className, ...rest } = props;
    const { headerId, setHeaderMounted } = useModalContext();
    React6.useEffect(() => {
      setHeaderMounted(true);
      return () => setHeaderMounted(false);
    }, [setHeaderMounted]);
    const _className = cx(
      "nature-modal__header",
      "nature-modal__header p-4 font-bold text-xl",
      className
    );
    const opts = {
      ref,
      className: _className,
      id: headerId,
      ...rest
    };
    return /* @__PURE__ */ jsx3(nature2.header, {
      ...opts
    });
  }
);
if (__DEV__) {
  ModalHeader.displayName = "ModalHeader";
}
var ModalBody = forwardRef((props, ref) => {
  const { className, ...rest } = props;
  const { bodyId, setBodyMounted } = useModalContext();
  React6.useEffect(() => {
    setBodyMounted(true);
    return () => setBodyMounted(false);
  }, [setBodyMounted]);
  const _className = cx(
    "nature-modal__body",
    "flex-1 py-2 px-4 overflow-auto",
    className
  );
  return /* @__PURE__ */ jsx3(nature2.div, {
    ref,
    className: _className,
    id: bodyId,
    ...rest
  });
});
if (__DEV__) {
  ModalBody.displayName = "ModalBody";
}
var ModalFooter = forwardRef(
  (props, ref) => {
    const { className, ...rest } = props;
    const _className = cx(
      "nature-modal__footer",
      "flex items-center justify-end p-4",
      className
    );
    return /* @__PURE__ */ jsx3(nature2.footer, {
      ref,
      className: _className,
      ...rest,
      css: { flex: 0, ...props.css }
    });
  }
);
if (__DEV__) {
  ModalFooter.displayName = "ModalFooter";
}
var ModalCloseButton = forwardRef((props, ref) => {
  const { onClick, className, ...rest } = props;
  const { onClose } = useModalContext();
  const _className = cx(
    "nature-modal__close-button",
    "absolute top-0 right-0 mt-3 mr-3",
    className
  );
  return /* @__PURE__ */ jsx3(CloseButton, {
    ...rest,
    ref,
    className: _className,
    onClick: callAllHandler2(onClick, (event) => {
      event.stopPropagation();
      onClose();
    })
  });
});
if (__DEV__) {
  ModalCloseButton.displayName = "ModalCloseButton";
}
var Modal = (props) => {
  const {
    overlayProps,
    contentProps,
    headerProps,
    bodyProps,
    footerProps,
    closeButtonProps,
    hideCloseButton,
    title,
    children,
    footer,
    ...rest
  } = props;
  return /* @__PURE__ */ jsxs(NatureModal, {
    ...rest,
    children: [
      /* @__PURE__ */ jsx3(ModalOverlay, {
        ...overlayProps
      }),
      /* @__PURE__ */ jsxs(ModalContent, {
        ...contentProps,
        children: [
          /* @__PURE__ */ jsx3(ModalHeader, {
            ...headerProps,
            children: title
          }),
          !hideCloseButton && /* @__PURE__ */ jsx3(ModalCloseButton, {
            ...closeButtonProps
          }),
          /* @__PURE__ */ jsx3(ModalBody, {
            ...contentProps,
            children
          }),
          /* @__PURE__ */ jsx3(ModalFooter, {
            ...footerProps,
            children: footer
          })
        ]
      })
    ]
  });
};
export {
  AlertDialog,
  ModalBody as AlertDialogBody,
  ModalCloseButton as AlertDialogCloseButton,
  AlertDialogContent,
  ModalFooter as AlertDialogFooter,
  ModalHeader as AlertDialogHeader,
  ModalOverlay as AlertDialogOverlay,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalContextProvider,
  ModalFocusScope,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NatureModal,
  useAriaHidden,
  useModal,
  useModalContext
};
